"use client";

/**
 * Discord link coverage — the instrument behind the decision to retire the
 * Zoho webhook.
 *
 * Two figures gate that decision, and they are deliberately the loudest things
 * on the panel:
 *
 *   Conflicts  — the Hub and the legacy `discord_users` table disagree about
 *                which Discord account belongs to a member. Must be zero.
 *   Zoho-only  — a legacy row with no verified counterpart in the Hub. Every
 *                one of these is coverage that would be LOST on the day the
 *                webhook is switched off.
 *
 * Everything else here is context for those two numbers.
 */

import { useCallback, useEffect, useState } from "react";
import { Check, ChevronDown, ChevronRight, Copy, Search } from "lucide-react";
import { fetchWithAuth } from "@/lib/auth";
import { DiscordVerifyMessageCard } from "./DiscordVerifyMessageCard";

const DARK = "#003d45";

interface RecentLink {
    user_id: string;
    name: string | null;
    email: string;
    discord_username: string | null;
    discord_id: string | null;
    source: string | null;
    verified_at: string | null;
    /** Managed roles held when last checked. null + roles_checked_at = not in the server. */
    roles: string[] | null;
    roles_checked_at: string | null;
    last_change: RoleChange | null;
}

interface RoleChange {
    granted: string[];
    removed: string[];
    source: string;
    at: string | null;
}

interface Analytics {
    total_active: number;
    linked: number;
    link_rate: number;
    by_oauth: number;
    by_backfill: number;
    non_members: number;
    legacy_total: number | null;
    conflicts: number | null;
    zoho_only: number | null;
    recent_links: RecentLink[];
}

export function DiscordPanel() {
    const [data, setData] = useState<Analytics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    // Which row's force-unlink is confirming, and which is in flight.
    const [confirmUnlink, setConfirmUnlink] = useState<string | null>(null);
    const [unlinking, setUnlinking] = useState<string | null>(null);

    // Nothing is set synchronously here: `loading` starts true and the first
    // state write happens after the fetch resolves, so mounting does not
    // trigger a cascading render.
    const load = useCallback(async () => {
        try {
            const res = await fetchWithAuth("/api/v1/staff/discord/analytics");
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setData(json);
            setError("");
        } catch {
            setError("Could not load Discord coverage.");
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Clear a member's link on their behalf.
     *
     * The backfill marked every imported row verified, so a Zoho mapping that
     * put a snowflake on the wrong person is a link the *rightful* owner cannot
     * clear: they hit "already connected to another member" at the callback and
     * the row they need is on somebody else's account. This is the way out.
     */
    const forceUnlink = useCallback(async (userId: string) => {
        setUnlinking(userId);
        try {
            const res = await fetchWithAuth(`/api/v1/staff/discord/unlink/${userId}`, { method: "POST" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setConfirmUnlink(null);
            await load();
        } catch {
            setError("Could not clear that link. Please try again.");
        } finally {
            setUnlinking(null);
        }
    }, [load]);

    // Fetch on mount. The rule fires because `load` writes state, but every
    // write happens after the awaited fetch, so there is no synchronous
    // cascade — this is the same shape every other staff panel uses.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => { void load(); }, [load]);

    if (loading) return <div className="p-6 text-sm text-[#666]">Loading…</div>;
    if (error || !data) return <div className="p-6 text-sm text-[#b3261e]">{error || "No data."}</div>;

    const retirementReady = data.conflicts === 0 && data.zoho_only === 0;

    return (
        <div className="space-y-6 p-1">
            <div>
                <h2 className="text-lg font-extrabold" style={{ color: DARK }}>Discord</h2>
                <p className="mt-1 text-xs text-[#666]">
                    Account link coverage, and what still depends on the Zoho webhook.
                </p>
            </div>

            <DiscordVerifyMessageCard />

            {/* ── Coverage ───────────────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <Tile label="Linked accounts" value={data.linked.toLocaleString()} sub={`${data.link_rate}% of active members`} />
                <Tile label="Active members" value={data.total_active.toLocaleString()} sub="denominator" />
                <Tile label="Self-verified" value={data.by_oauth.toLocaleString()} sub="member linked it themselves" />
                <Tile label="Backfilled" value={data.by_backfill.toLocaleString()} sub="imported from Zoho" />
                <Tile
                    label="No CRM record"
                    value={(data.non_members ?? 0).toLocaleString()}
                    sub="verified only, never member"
                />
            </div>

            {/* ── The retirement gate ────────────────────────────────── */}
            <div className="rounded-xl border border-hairline p-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-extrabold" style={{ color: DARK }}>
                        Zoho webhook retirement
                    </h3>
                    <span
                        className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                            data.conflicts === null
                                ? "bg-cultured text-[#666]"
                                : retirementReady
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "bg-amber-50 text-amber-800"
                        }`}
                    >
                        {data.conflicts === null ? "legacy table gone" : retirementReady ? "ready" : "not yet"}
                    </span>
                </div>

                {data.conflicts === null ? (
                    <p className="mt-3 text-xs leading-relaxed text-[#666]">
                        The legacy <code>discord_users</code> table is no longer present — the webhook has
                        been retired and the Hub is the sole source of Discord identity.
                    </p>
                ) : (
                    <>
                        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
                            <Tile
                                label="Conflicts"
                                value={String(data.conflicts)}
                                sub="sources disagree — must be 0"
                                tone={data.conflicts ? "bad" : "good"}
                            />
                            <Tile
                                label="Zoho-only"
                                value={String(data.zoho_only)}
                                sub="coverage lost if switched off today"
                                tone={data.zoho_only ? "warn" : "good"}
                            />
                            <Tile label="Legacy rows" value={(data.legacy_total ?? 0).toLocaleString()} sub="in discord_users" />
                        </div>
                        <p className="mt-3 text-xs leading-relaxed text-[#666]">
                            {retirementReady
                                ? "Every legacy Discord ID has a verified counterpart in the Hub and no source disagrees. Safe to disable the webhook's writes."
                                : "Work Zoho-only down by getting those members to link their own account, then re-run the backfill. Conflicts need a human — they are usually one person holding two member records."}
                        </p>
                    </>
                )}
            </div>

            <RecentLinks
                recent={data.recent_links}
                onReload={load}
                confirmUnlink={confirmUnlink}
                setConfirmUnlink={setConfirmUnlink}
                unlinking={unlinking}
                forceUnlink={forceUnlink}
            />

            <UnlinkedRoleHolders />
        </div>
    );
}

const LINK_SOURCE_LABELS: Record<string, string> = {
    link: "Connect Discord",
    refresh: "profile refresh",
    zoho: "Zoho form",
    reconcile: "4-hourly sync",
};

/**
 * Linked accounts: the latest 20 by default, or a server-side search across
 * every link (name, email, Discord username, or an exact numeric ID).
 */
function RecentLinks({
    recent, onReload, confirmUnlink, setConfirmUnlink, unlinking, forceUnlink,
}: {
    recent: RecentLink[];
    onReload: () => Promise<void>;
    confirmUnlink: string | null;
    setConfirmUnlink: (id: string | null) => void;
    unlinking: string | null;
    forceUnlink: (id: string) => Promise<void>;
}) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<{ q: string; total: number; links: RecentLink[] } | null>(null);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState("");
    const [expanded, setExpanded] = useState<string | null>(null);

    const search = useCallback(async (q: string, offset = 0) => {
        setSearching(true);
        setSearchError("");
        try {
            const params = new URLSearchParams({ q, limit: "50", offset: String(offset) });
            const res = await fetchWithAuth(`/api/v1/staff/discord/links?${params}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json: { total: number; links: RecentLink[] } = await res.json();
            setResults((prev) => ({
                q,
                total: json.total,
                links: offset && prev?.q === q ? [...prev.links, ...json.links] : json.links,
            }));
        } catch {
            setSearchError("Search failed. Please try again.");
        } finally {
            setSearching(false);
        }
    }, []);

    // Debounced: every keystroke would otherwise be a query over all links.
    useEffect(() => {
        const q = query.trim();
        if (!q) return;
        const t = setTimeout(() => { void search(q); }, 300);
        return () => clearTimeout(t);
    }, [query, search]);

    const active = query.trim() !== "";
    const rows = active ? (results?.q === query.trim() ? results.links : []) : recent;

    const reload = async () => {
        await onReload();
        if (active) await search(query.trim());
    };

    return (
        <div className="rounded-xl border border-hairline">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline px-4 py-3">
                <h3 className="text-sm font-extrabold" style={{ color: DARK }}>
                    {active ? "Linked accounts" : "Recent links"}
                </h3>
                <div className="flex items-center gap-3">
                    <SearchBox
                        value={query}
                        onChange={setQuery}
                        placeholder="Search name, email, @username or Discord ID"
                    />
                    <button onClick={() => void reload()} className="text-xs font-bold text-[#666] hover:underline">
                        Refresh
                    </button>
                </div>
            </div>

            {active && (
                <p className="px-4 pt-3 text-[11px] text-[#888]">
                    {searching && !results ? "Searching…"
                        : searchError ? <span className="text-[#b3261e]">{searchError}</span>
                            : results?.q === query.trim()
                                ? `${results.total.toLocaleString()} match${results.total === 1 ? "" : "es"}`
                                : "Searching…"}
                </p>
            )}

            {rows.length === 0 ? (
                <p className="px-4 py-6 text-xs text-[#666]">
                    {active ? (searching ? "" : "No linked account matches.") : "Nobody has linked an account yet."}
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                        <thead className="text-[10px] uppercase tracking-wide text-[#888]">
                            <tr className="border-b border-hairline">
                                <Th>Member</Th><Th>Discord</Th><Th>Roles held</Th><Th>Last role change</Th>
                                <Th>Source</Th><Th>Linked</Th><Th>{""}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <LinkRow
                                    key={r.user_id}
                                    r={r}
                                    expanded={expanded === r.user_id}
                                    onToggle={() => setExpanded(expanded === r.user_id ? null : r.user_id)}
                                    confirming={confirmUnlink === r.user_id}
                                    setConfirmUnlink={setConfirmUnlink}
                                    unlinking={unlinking === r.user_id}
                                    forceUnlink={forceUnlink}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {active && results?.q === query.trim() && results.links.length < results.total && (
                <div className="border-t border-hairline px-4 py-2.5 text-center">
                    <button
                        onClick={() => void search(query.trim(), results.links.length)}
                        disabled={searching}
                        className="text-xs font-bold text-[#666] hover:underline disabled:opacity-60"
                    >
                        {searching ? "Loading…" : `Show more (${(results.total - results.links.length).toLocaleString()} left)`}
                    </button>
                </div>
            )}
        </div>
    );
}

function LinkRow({
    r, expanded, onToggle, confirming, setConfirmUnlink, unlinking, forceUnlink,
}: {
    r: RecentLink;
    expanded: boolean;
    onToggle: () => void;
    confirming: boolean;
    setConfirmUnlink: (id: string | null) => void;
    unlinking: boolean;
    forceUnlink: (id: string) => Promise<void>;
}) {
    return (
        <>
            <tr className="border-b border-hairline/60 align-top last:border-0">
                <Td>
                    <button onClick={onToggle} className="flex items-start gap-1 text-left" title="Role history">
                        {expanded
                            ? <ChevronDown className="mt-0.5 h-3 w-3 shrink-0 text-[#888]" />
                            : <ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-[#888]" />}
                        <span>
                            <span className="font-semibold">{r.name || "—"}</span>
                            <span className="block text-[#888]">{r.email}</span>
                        </span>
                    </button>
                </Td>
                <Td>
                    {r.discord_username ? <span>@{r.discord_username}</span> : <span className="text-[#888]">no username</span>}
                    {r.discord_id && <DiscordId id={r.discord_id} />}
                </Td>
                <Td><RolesHeld roles={r.roles} checkedAt={r.roles_checked_at} /></Td>
                <Td>
                    {r.last_change ? <ChangeLine c={r.last_change} /> : <span className="text-[#888]">—</span>}
                </Td>
                <Td>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                        r.source === "oauth" ? "bg-emerald-50 text-emerald-700" : "bg-cultured text-[#666]"
                    }`}>
                        {r.source === "oauth" ? "self" : "backfill"}
                    </span>
                </Td>
                <Td className="whitespace-nowrap text-[#666]">{fmt(r.verified_at)}</Td>
                <Td>
                    {confirming ? (
                        <span className="flex items-center gap-2 whitespace-nowrap">
                            <button
                                onClick={() => void forceUnlink(r.user_id)}
                                disabled={unlinking}
                                className="font-bold text-[#b3261e] hover:underline disabled:opacity-60"
                            >
                                {unlinking ? "Clearing…" : "Confirm"}
                            </button>
                            <button onClick={() => setConfirmUnlink(null)} className="text-[#888] hover:underline">
                                Cancel
                            </button>
                        </span>
                    ) : (
                        <button
                            onClick={() => setConfirmUnlink(r.user_id)}
                            className="whitespace-nowrap text-[#888] hover:underline"
                            title="Frees this Discord account so the right member can claim it. Server roles are left alone."
                        >
                            Clear link
                        </button>
                    )}
                </Td>
            </tr>
            {expanded && (
                <tr className="border-b border-hairline/60 bg-cultured/40">
                    <td colSpan={7} className="px-4 py-3">
                        <RoleHistory userId={r.user_id} />
                    </td>
                </tr>
            )}
        </>
    );
}

function RoleHistory({ userId }: { userId: string }) {
    const [events, setEvents] = useState<RoleChange[] | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchWithAuth(`/api/v1/staff/discord/links/${userId}/role-history`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json: RoleChange[] = await res.json();
                if (!cancelled) setEvents(json);
            } catch {
                if (!cancelled) setError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [userId]);

    if (error) return <p className="text-[11px] text-[#b3261e]">Could not load role history.</p>;
    if (!events) return <p className="text-[11px] text-[#888]">Loading…</p>;
    if (events.length === 0) {
        return (
            <p className="text-[11px] text-[#888]">
                No role changes recorded. History starts on 17 Oct 2026; earlier grants were not logged with role names.
            </p>
        );
    }
    return (
        <ul className="space-y-1">
            {events.map((e, i) => (
                <li key={i}><ChangeLine c={e} /></li>
            ))}
        </ul>
    );
}

function ChangeLine({ c }: { c: RoleChange }) {
    return (
        <span className="leading-relaxed">
            {c.granted.map((g) => (
                <span key={`+${g}`} className="mr-1 font-semibold text-emerald-700">+{roleLabel(g)}</span>
            ))}
            {c.removed.map((g) => (
                <span key={`-${g}`} className="mr-1 font-semibold text-[#b3261e]">−{roleLabel(g)}</span>
            ))}
            <span className="block text-[10px] text-[#888]">
                {fmtTime(c.at)} · {LINK_SOURCE_LABELS[c.source] ?? c.source}
            </span>
        </span>
    );
}

function RolesHeld({ roles, checkedAt }: { roles: string[] | null; checkedAt: string | null }) {
    if (!checkedAt) return <span className="text-[#888]" title="Filled in by the next 4-hourly sync">not checked yet</span>;
    if (roles === null) {
        return (
            <span className="text-amber-800" title={`as of ${fmtTime(checkedAt)}`}>
                not in the server
            </span>
        );
    }
    return (
        <span className="block">
            <span className="flex flex-wrap gap-1">
                {roles.length === 0
                    ? <span className="text-[#888]">none of ours</span>
                    : roles.map((role) => (
                        <span key={role} className="whitespace-nowrap rounded bg-cultured px-1.5 py-0.5 text-[10px] font-bold text-[#444]">
                            {roleLabel(role)}
                        </span>
                    ))}
            </span>
            <span className="mt-0.5 block text-[10px] text-[#888]">as of {fmtTime(checkedAt)}</span>
        </span>
    );
}

/** Snowflakes stay strings end to end — a JS number would corrupt them. */
function DiscordId({ id }: { id: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(id);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked — the id is still selectable */ }
    };
    return (
        <span className="mt-0.5 flex items-center gap-1 font-mono text-[11px] text-[#666]">
            <span className="select-all">{id}</span>
            <button onClick={() => void copy()} className="text-[#888] hover:text-[#444]" title="Copy Discord ID">
                {copied ? <Check className="h-3 w-3 text-emerald-700" /> : <Copy className="h-3 w-3" />}
            </button>
        </span>
    );
}

function SearchBox({
    value, onChange, placeholder,
}: { value: string; onChange: (v: string) => void; placeholder: string }) {
    return (
        <label className="flex w-72 max-w-full items-center gap-2 rounded-lg border border-hairline px-2.5 py-1.5 focus-within:border-steel">
            <Search className="h-3.5 w-3.5 shrink-0 text-[#888]" />
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full bg-transparent text-xs outline-none"
            />
            {value && (
                <button onClick={() => onChange("")} className="text-[10px] font-bold text-[#888] hover:underline">
                    Clear
                </button>
            )}
        </label>
    );
}

interface UnlinkedHolder {
    discord_id: string;
    username: string | null;
    global_name: string | null;
    nick: string | null;
    roles: string[];
    crm_hint: { member_id: number; journey: string | null; expiry: string | null }[];
    lapsed: boolean;
}

interface UnlinkedReport {
    computed_at: string;
    total: number;
    counts: Record<string, number>;
    holders: UnlinkedHolder[];
}

const JOURNEY_LABELS: Record<string, string> = {
    charterholder: "Charterholder",
    charterholderinactive: "Charterholder (inactive)",
    charterpending: "Charter pending",
    charterpending_inactive: "Charter pending (inactive)",
    level3candidate: "L3 candidate",
    level2candidate: "L2 candidate",
    level1candidate: "L1 candidate",
    l3prospect: "L3 prospect",
    l2prospect: "L2 prospect",
    l1prospect: "L1 prospect",
};

const journeyLabel = (j: string | null) => (j ? JOURNEY_LABELS[j] ?? j : "no role");
const roleLabel = (r: string) =>
    r === "verified" ? "Verified" : r === "member" ? "Member" : journeyLabel(r);

/**
 * Journey-role holders the reconciler cannot reach: their Discord account is
 * linked to no CRM record, so their role never changes however their
 * membership does (backend `app/discord/orphans.py`). Written by each ETL
 * cycle; this only reads the snapshot. The CRM match is by display name and is
 * a hint for a moderator, never something the system acts on.
 */
function UnlinkedRoleHolders() {
    const [report, setReport] = useState<UnlinkedReport | null>(null);
    const [state, setState] = useState<"loading" | "ready" | "empty" | "error">("loading");
    const [role, setRole] = useState("charterholder");
    const [lapsedOnly, setLapsedOnly] = useState(false);
    const [query, setQuery] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchWithAuth("/api/v1/staff/discord/unlinked-holders");
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json: UnlinkedReport | null = await res.json();
                if (cancelled) return;
                setReport(json);
                setState(json ? "ready" : "empty");
            } catch {
                if (!cancelled) setState("error");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const q = query.trim().toLowerCase().replace(/^@/, "");
    const matches = (h: UnlinkedHolder) =>
        !q || h.discord_id === q || [h.global_name, h.nick, h.username].some((n) => n?.toLowerCase().includes(q));
    const rows = (report?.holders ?? []).filter(
        (h) => (role === "all" || h.roles.includes(role)) && (!lapsedOnly || h.lapsed) && matches(h),
    );

    return (
        <div className="rounded-xl border border-hairline">
            <div className="border-b border-hairline px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-extrabold" style={{ color: DARK }}>Role holders we can&apos;t manage</h3>
                    {report && <span className="text-[10px] text-[#888]">as of {fmtTime(report.computed_at)}</span>}
                </div>
                <div className="mt-2 space-y-1.5 text-xs leading-relaxed text-[#666]">
                    <p>
                        These Discord accounts hold a CMT journey role (Charterholder, L2 candidate and so on) but
                        aren&apos;t connected to any Volunteer Hub account. Most got the role by hand, or from an older
                        bot, before linking existed.
                    </p>
                    <p>
                        Because the Hub doesn&apos;t know who they are, the 4-hourly sync can&apos;t update their role.
                        A lapsed charterholder in this list keeps Charterholder.{" "}
                        <b>It sorts itself out when the person clicks Connect Discord.</b> Until then, only a moderator
                        can change their role in Discord.
                    </p>
                    <p>
                        <b>CRM namesakes</b> are CMT records whose name matches the Discord display name. That&apos;s a
                        hint for a moderator, not a confirmed identity, and the Hub never acts on it.
                    </p>
                </div>
            </div>

            {state === "loading" && <p className="px-4 py-6 text-xs text-[#666]">Loading…</p>}
            {state === "error" && <p className="px-4 py-6 text-xs text-[#b3261e]">Could not load the report.</p>}
            {state === "empty" && (
                <p className="px-4 py-6 text-xs text-[#666]">No report yet. It appears after the next ETL cycle.</p>
            )}

            {state === "ready" && report && (
                <>
                    <div className="flex flex-wrap items-center gap-1.5 px-4 pt-3">
                        {[["all", report.total] as const, ...Object.entries(report.counts)].map(([key, n]) => (
                            <button
                                key={key}
                                onClick={() => setRole(key)}
                                className={`rounded-full border px-2.5 py-1 text-[10px] font-bold ${
                                    role === key ? "border-transparent text-white" : "border-hairline text-[#666]"
                                }`}
                                style={role === key ? { background: DARK } : undefined}
                            >
                                {key === "all" ? "All" : journeyLabel(key)} · {n}
                            </button>
                        ))}
                        <label
                            className="ml-auto flex items-center gap-1.5 text-[10px] font-bold text-[#666]"
                            title="Every CRM record with this name has lapsed, so the account probably holds a role it no longer qualifies for"
                        >
                            <input type="checkbox" checked={lapsedOnly} onChange={(e) => setLapsedOnly(e.target.checked)} />
                            Lapsed namesakes only
                        </label>
                    </div>
                    <div className="px-4 pt-3">
                        <SearchBox value={query} onChange={setQuery} placeholder="Search Discord name or ID" />
                    </div>
                    {rows.length === 0 ? (
                        <p className="px-4 py-6 text-xs text-[#666]">Nobody matches this filter.</p>
                    ) : (
                        <div className="mt-2 max-h-[480px] overflow-auto">
                            <table className="w-full text-left text-xs">
                                <thead className="sticky top-0 bg-white text-[10px] uppercase tracking-wide text-[#888]">
                                    <tr className="border-b border-hairline">
                                        <Th>Discord</Th><Th>ID</Th><Th>Roles held</Th><Th>CRM namesakes</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((h) => (
                                        <tr key={h.discord_id} className="border-b border-hairline/60 last:border-0">
                                            <Td>
                                                {h.global_name || h.nick || h.username || "—"}
                                                {h.username && <span className="ml-1 text-[#888]">@{h.username}</span>}
                                            </Td>
                                            <Td className="font-mono text-[#666]">{h.discord_id}</Td>
                                            <Td>{h.roles.map(journeyLabel).join(", ")}</Td>
                                            <Td className={h.lapsed ? "text-amber-800" : "text-[#666]"}>
                                                {h.crm_hint.length === 0
                                                    ? "—"
                                                    : h.crm_hint
                                                        .map((m) => `#${m.member_id} ${journeyLabel(m.journey)}${m.expiry ? ` (exp ${m.expiry})` : ""}`)
                                                        .join("; ")}
                                            </Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Tile({
    label, value, sub, tone,
}: { label: string; value: string; sub?: string; tone?: "good" | "warn" | "bad" }) {
    const toneCls =
        tone === "bad" ? "text-[#b3261e]" : tone === "warn" ? "text-amber-700" : tone === "good" ? "text-emerald-700" : "";
    return (
        <div className="rounded-xl border border-hairline p-3">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#888]">{label}</div>
            <div className={`mt-1 text-2xl font-extrabold ${toneCls}`} style={tone ? undefined : { color: DARK }}>
                {value}
            </div>
            {sub && <div className="mt-0.5 text-[10px] leading-tight text-[#888]">{sub}</div>}
        </div>
    );
}

function Th({ children }: { children: React.ReactNode }) {
    return <th className="px-4 py-2 font-extrabold">{children}</th>;
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
    return <td className={`px-4 py-2.5 ${className}`}>{children}</td>;
}

function fmt(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function fmtTime(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? "—"
        : d.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default DiscordPanel;
